// ─────────────────────────────────────────────────────────────────────────────
// LIH Psychograph Engine  v1.0
// Phase 5 — Derives voice profile from assessment answers.
//
// Output: { voice, dimensions, scores, signals }
//   voice: 'ANALYST' | 'SEEKER' | 'PRAGMATIST'
//
// ANALYST   — mechanistic cognitive style, high science receptivity,
//             optimizer/pragmatist motivation. Leads with mechanism.
// SEEKER    — holistic cognitive style, seeker motivation frame.
//             Leads with meaning and integration.
// PRAGMATIST — pragmatic style or minimal signals.
//             Leads with action. No preamble.
// ─────────────────────────────────────────────────────────────────────────────

export function computePsychograph(answers) {
  const a = answers || {};

  // ── Open text analysis ───────────────────────────────────────────────────
  const openText     = (a.q_open || '').trim();
  const openWordCount = openText.split(/\s+/).filter(Boolean).length;
  const openLower    = openText.toLowerCase();

  const mechanisticTerms = [
    'mechanism', 'optimize', 'performance', 'data', 'metric', 'protocol',
    'efficiency', 'output', 'track', 'measure', 'hrv', 'vo2', 'biomarker',
    'research', 'study', 'evidence', 'clinical', 'physiolog', 'biochem',
    'cellular', 'molecular', 'quantif', 'baseline', 'benchmark', 'biohack',
  ];
  const holisticTerms = [
    'balance', 'whole', 'connect', 'meaning', 'purpose', 'journey',
    'integrated', 'harmony', 'wellbeing', 'well-being', 'presence', 'aware',
    'mindful', 'spirit', 'fulfil', 'alive', 'vibrant', 'thrive', 'flow',
    'peace', 'grounded', 'authentic', 'nourish', 'transform', 'intention',
  ];

  const mechText  = mechanisticTerms.filter(w => openLower.includes(w)).length;
  const holistText = holisticTerms.filter(w => openLower.includes(w)).length;

  // ── Practice signals ─────────────────────────────────────────────────────
  const mindbody    = a.q_mindbody || [];
  const hasTM       = mindbody.some(p =>
    p.toLowerCase().includes('transcendental') || p.toLowerCase().includes(' tm'));
  const hasYoga     = mindbody.some(p => p.toLowerCase().includes('yoga'));
  const hasMed      = mindbody.some(p => p.toLowerCase().includes('meditat'));
  const hasMindbody = !mindbody.includes('None currently') && mindbody.length > 0;

  // ── Exercise signals ─────────────────────────────────────────────────────
  const exerciseType = a.q14_exercise_type || [];
  const isAthlete    = (a.q13_exercise || '').includes('Very active') ||
                       (a.q13_exercise || '').includes('Active — regular exercise 3–4');
  const isTracking   = (a.q10_diet_type || []).some(d =>
    d.toLowerCase().includes('macro') || d.toLowerCase().includes('track'));
  const doesPrecision = exerciseType.some(t =>
    t.toLowerCase().includes('row') || t.toLowerCase().includes('cycl') ||
    t.toLowerCase().includes('swim'));

  // ── Goals signals ────────────────────────────────────────────────────────
  const goalsRaw = [
    a.q3_goals,
    ...(Array.isArray(a.q3_goals) ? a.q3_goals : []),
    a.q_goals,
  ].filter(Boolean).join(' ').toLowerCase();

  const optimizerTerms  = ['performance', 'athletic', 'muscle', 'endurance', 'longevity',
    'cognitive', 'focus', 'optim', 'peak', 'enhance', 'sharpen'];
  const seekerTerms     = ['wellbeing', 'balance', 'mood', 'stress', 'overall health',
    'quality of life', 'feel better', 'whole', 'flourish'];
  const pragmatistTerms = ['weight', 'energy', 'maintain', 'basic', 'keep up', 'healthy'];

  const optGoal  = optimizerTerms.filter(g => goalsRaw.includes(g)).length;
  const seekGoal = seekerTerms.filter(g => goalsRaw.includes(g)).length;
  const pragGoal = pragmatistTerms.filter(g => goalsRaw.includes(g)).length;

  // ── Lifestyle signals ────────────────────────────────────────────────────
  const civic      = a.q_civic || '';
  const arts       = a.q_arts  || '';
  const civicActive = civic.length > 0 &&
    !civic.includes('No') && !civic.includes('thinking') && !civic.includes('considering');
  const artsActive  = arts.length > 0 &&
    !arts.includes('No') && !arts.includes('used to') && !arts.includes('curious');

  // ── Dimension 1: Cognitive Style ─────────────────────────────────────────
  let mechScore = 0, holistScore = 0, pragScore = 0;

  mechScore  += mechText  * 2;
  holistScore += holistText * 2;

  if (hasTM)              holistScore += 3;
  if (hasYoga || hasMed)  holistScore += 2;
  if (isTracking || doesPrecision) mechScore += 2;
  if (civicActive)        holistScore += 1;
  if (artsActive)         holistScore += 2;
  if (isAthlete)          mechScore   += 1;

  if (openWordCount > 0 && openWordCount < 8)   pragScore += 2;
  else if (openWordCount > 50) { mechScore += 1; holistScore += 1; }

  if (!hasMindbody) pragScore += 1;

  // ── Dimension 2: Motivation Frame ────────────────────────────────────────
  let optScore  = optGoal  * 2;
  let seekScore = seekGoal;
  let pragMot   = pragGoal;

  if (civicActive) seekScore += 2;
  if (artsActive)  seekScore += 1;
  if (hasTM)       seekScore += 2;
  if (isAthlete)   optScore  += 2;
  if (mechText > 1) optScore += 1;

  // ── Dimension 3: Science Receptivity ─────────────────────────────────────
  const sciScore = mechText * 2 + (isTracking ? 2 : 0) +
    (openWordCount > 40 ? 1 : 0) + (isAthlete ? 1 : 0) + (doesPrecision ? 1 : 0);
  const scienceReceptivity = sciScore >= 4 ? 'high' : sciScore <= 1 ? 'low' : 'moderate';

  // ── Dimension 4: Challenge Appetite ──────────────────────────────────────
  const stressRaw = (a.q16_stress || '').split('—')[0].trim();
  const highStress = ['High', 'Very high'].includes(stressRaw);
  const sedentary  = (a.q13_exercise || '').includes('Sedentary');

  const challengeAppetite =
    (!highStress && (isAthlete || scienceReceptivity === 'high')) ? 'push' :
    (highStress || sedentary) ? 'support' : 'neutral';

  // ── Voice Resolution ──────────────────────────────────────────────────────
  const cogMax  = Math.max(mechScore, holistScore, pragScore);
  const cogStyle = mechScore === cogMax ? 'mechanistic'
    : holistScore === cogMax ? 'holistic' : 'pragmatic';

  const motMax  = Math.max(optScore, seekScore, pragMot);
  const motFrame = optScore === motMax ? 'optimizer'
    : seekScore === motMax ? 'seeker' : 'pragmatist';

  let voice;
  if (cogStyle === 'mechanistic' ||
      (scienceReceptivity === 'high' && motFrame === 'optimizer')) {
    voice = 'ANALYST';
  } else if (cogStyle === 'holistic' || motFrame === 'seeker') {
    voice = 'SEEKER';
  } else {
    voice = 'PRAGMATIST';
  }

  // Overrides
  // High science + seeker motivation + holistic style → meaning wins
  if (scienceReceptivity === 'high' && motFrame === 'seeker' && cogStyle === 'holistic') {
    voice = 'SEEKER';
  }
  // Blank / minimal signals → lowest-assumption voice
  if (openWordCount === 0 && !hasMindbody && !civicActive && !artsActive && !isAthlete) {
    voice = 'PRAGMATIST';
  }

  return {
    voice,
    dimensions: { cogStyle, motFrame, scienceReceptivity, challengeAppetite },
    scores:     { mechScore, holistScore, pragScore, optScore, seekScore, pragMot, sciScore },
    signals:    { hasTM, hasYoga, hasMed, hasMindbody, isAthlete, civicActive, artsActive,
                  isTracking, openWordCount, highStress, sedentary },
  };
}
